import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { seoApi } from "@/lib/seoApi";
import {
  ExternalLink, Eye, Copy, Check, ArrowUp, ArrowDown, Search as SearchIcon, X,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

const SORT_OPTIONS = [
  { value: "published_at", label: "Published at" },
  { value: "title", label: "Title" },
  { value: "cost", label: "Cost" },
  { value: "started_at", label: "Started" },
];

export default function PublishedArticles() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState(null);

  // Filters (URL-backed so pages are shareable/bookmarkable)
  const q = params.get("q") || "";
  const status = params.get("status") || "all";
  const sort = params.get("sort") || "published_at";
  const order = params.get("order") || "desc";
  const briefId = params.get("brief_id") || "";
  const offset = Number(params.get("offset") || 0);
  const limit = 50;

  const [qDraft, setQDraft] = useState(q);
  useEffect(() => setQDraft(q), [q]);

  // Debounce free-text search
  useEffect(() => {
    if (qDraft === q) return;
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (qDraft) next.set("q", qDraft);
      else next.delete("q");
      next.delete("offset");
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [qDraft, q, params, setParams]);

  function patch(kv) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(kv)) {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    }
    setParams(next, { replace: true });
  }

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const qs = new URLSearchParams({ sort, order, limit, offset });
    if (q) qs.set("q", q);
    if (status && status !== "all") qs.set("status", status);
    if (briefId) qs.set("brief_id", briefId);
    seoApi.get(`/seo/published?${qs.toString()}`)
      .then((r) => {
        if (cancel) return;
        setRows(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((err) => {
        if (cancel) return;
        toast.error(err?.response?.data?.detail || err.message);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [q, status, sort, order, briefId, offset]);

  function toggleSort(field) {
    if (sort === field) {
      patch({ order: order === "desc" ? "asc" : "desc" });
    } else {
      patch({ sort: field, order: "desc" });
    }
  }

  function copySlug(slug) {
    navigator.clipboard.writeText(slug);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 1200);
    toast.success("Slug copied");
  }

  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);

  return (
    <div className="space-y-6" data-testid="published-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Published Articles</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Everything SEO Studio has published to Payload CMS. Open articles for editing,
            jump back to their runs, or copy the public slug.
          </p>
        </div>
        {briefId && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => patch({ brief_id: null })}
            data-testid="clear-brief-filter"
          >
            <X size={14} /> Brief filter · clear
          </Button>
        )}
      </div>

      {/* Filters row */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="Search title, slug or brief…"
              className="pl-9"
              data-testid="search-input"
            />
          </div>

          <div className="flex items-center gap-1 border border-neutral-200 rounded-md p-1 bg-white">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => patch({ status: opt.value === "all" ? null : opt.value })}
                className={`text-xs px-3 py-1.5 rounded transition ${
                  (opt.value === status || (opt.value === "all" && !params.get("status")))
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-600 hover:text-neutral-900"
                }`}
                data-testid={`status-filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <span>Sort:</span>
            <select
              value={sort}
              onChange={(e) => patch({ sort: e.target.value })}
              className="border border-neutral-200 rounded px-2 py-1.5 bg-white text-neutral-900"
              data-testid="sort-select"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => patch({ order: order === "desc" ? "asc" : "desc" })}
              className="border border-neutral-200 rounded px-2 py-1.5 bg-white text-neutral-900 hover:border-neutral-400"
              data-testid="order-toggle"
              aria-label={`sort ${order}`}
            >
              {order === "desc" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Results table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-neutral-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center" data-testid="empty-state">
              <p className="text-sm text-neutral-600">
                {q || status !== "all" || briefId
                  ? "No published articles match the current filters."
                  : "No articles published yet. Generate one from a brief, then hit Publish to CMS."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="published-table">
              <thead className="text-xs text-neutral-500">
                <tr className="border-b border-neutral-200">
                  <Th onClick={() => toggleSort("title")} active={sort === "title"} order={order}>Title / Slug</Th>
                  <th className="text-left px-4 py-3">Status</th>
                  <Th onClick={() => toggleSort("published_at")} active={sort === "published_at"} order={order}>Published at</Th>
                  <th className="text-left px-4 py-3">Brief</th>
                  <th className="text-left px-4 py-3">Run</th>
                  <th className="text-left px-4 py-3">Version</th>
                  <Th onClick={() => toggleSort("cost")} active={sort === "cost"} order={order}>Cost</Th>
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.run_id} className="border-t border-neutral-100 hover:bg-neutral-50/50"
                      data-testid={`row-${r.run_id}`}>
                    <td className="px-6 py-3">
                      <div className="font-medium text-neutral-900">{r.title}</div>
                      <div className="text-xs text-neutral-500 font-mono mt-0.5">{r.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {r.published_at ? new Date(r.published_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {r.brief_id ? (
                        <Link
                          to={`/seo-studio/briefs/${r.brief_id}`}
                          className="underline hover:no-underline"
                          data-testid={`open-brief-${r.brief_id}`}
                        >
                          {r.brief_title || `${r.brief_id.slice(0, 8)}`}
                          {r.brief_version ? <span className="text-neutral-400"> · v{r.brief_version}</span> : null}
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link
                        to={`/seo-studio/runs/${r.run_id}`}
                        className="underline hover:no-underline"
                        data-testid={`open-run-${r.run_id}`}
                      >
                        {r.run_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {r.current_content_version
                        ? `v${r.current_content_version}${r.content_versions_count > 1 ? ` (${r.content_versions_count})` : ""}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-700 font-mono text-xs">
                      {typeof r.estimated_cost_usd === "number" ? `$${r.estimated_cost_usd.toFixed(6)}` : "—"}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <IconAction
                          href={r.cms_admin_url}
                          testId={`open-cms-${r.run_id}`}
                          title="Open in CMS"
                          icon={<ExternalLink size={14} />}
                          external
                        />
                        <IconAction
                          to={`/seo-studio/runs/${r.run_id}`}
                          testId={`view-run-${r.run_id}`}
                          title="Open run in SEO Studio"
                          icon={<Eye size={14} />}
                        />
                        <IconAction
                          onClick={() => copySlug(r.slug)}
                          testId={`copy-slug-${r.run_id}`}
                          title="Copy slug"
                          icon={copiedSlug === r.slug ? <Check size={14} /> : <Copy size={14} />}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination footer */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <div data-testid="pagination-info">
            Showing {showingFrom}–{showingTo} of {total}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => patch({ offset: Math.max(0, offset - limit) })}
              disabled={offset === 0}
              data-testid="prev-page"
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => patch({ offset: offset + limit })}
              disabled={offset + rows.length >= total}
              data-testid="next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    published: "bg-emerald-100 text-emerald-800 border-emerald-200",
    draft: "bg-amber-100 text-amber-800 border-amber-200",
  };
  return (
    <Badge
      variant="outline"
      className={map[status] || "bg-neutral-100 text-neutral-700"}
      data-testid={`status-badge-${status}`}
    >
      {status}
    </Badge>
  );
}

function Th({ children, onClick, active, order }) {
  return (
    <th className="text-left px-4 py-3">
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={`inline-flex items-center gap-1 hover:text-neutral-900 ${
            active ? "text-neutral-900 font-medium" : ""
          }`}
        >
          {children}
          {active && (order === "desc" ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
        </button>
      ) : children}
    </th>
  );
}

function IconAction({ href, to, onClick, icon, title, testId, external }) {
  const cn =
    "inline-flex items-center justify-center h-8 w-8 rounded border border-neutral-200 hover:border-neutral-400 hover:bg-white text-neutral-600 hover:text-neutral-900 transition";
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer noopener" : undefined}
        className={cn}
        title={title}
        data-testid={testId}
      >
        {icon}
      </a>
    );
  }
  if (to) {
    return (
      <Link to={to} className={cn} title={title} data-testid={testId}>
        {icon}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cn} title={title} data-testid={testId}>
      {icon}
    </button>
  );
}
